/**
 * Editor Bridge - PostMessage communication for iframe mode
 * 
 * Provides bidirectional communication between the iframe (editor logic)
 * and parent window (editor UI).
 */

import type { EditorMode } from '../types';

// ============================================================================
// Message Types
// ============================================================================

export interface ElementEditData {
  selector: string;
  tagName: string;
  textContent: string;
  computedStyles: Record<string, string>;
}

/** Page structure summary for AI context */
export interface PageStructureSummary {
  title: string;
  headings: Array<{ text: string; level: number; selector: string }>;
  buttons: Array<{ text: string; selector: string }>;
  links: Array<{ text: string; href: string; selector: string }>;
  images: Array<{ alt: string; src: string; selector: string }>;
  forms: Array<{ selector: string; fields: number }>;
}

export type EditorMessage =
  | { type: 'READY' }
  | { type: 'PAGE_STRUCTURE'; payload: PageStructureSummary }
  | { type: 'ELEMENT_SELECTED'; payload: { selector: string; bounds: DOMRect; tagName: string; textContent: string; computedStyles?: Record<string, string> } }
  | { type: 'ELEMENT_CLICKED_INTENT'; payload: { selector: string; bounds: DOMRect; tagName: string; textContent: string; computedStyles: Record<string, string> } }
  | { type: 'ELEMENT_HOVERED'; payload: { selector: string; bounds: DOMRect } }
  | { type: 'MODE_CHANGED'; payload: { mode: EditorMode } }
  | { type: 'STATE_CHANGED'; payload: { hasUnsavedChanges: boolean; instructionCount: number; historyIndex: number; historyLength: number } }
  | { type: 'OPEN_EDITOR'; payload: { element: ElementEditData & { src?: string; alt?: string }; initialTab: 'text' | 'styles' | 'image' } }
  | { type: 'CLOSE_EDITOR' }
  | { type: 'STYLE_PREVIEW'; payload: { selector: string; property: string; value: string } }
  | { type: 'ERROR'; payload: { message: string; stack?: string } }
  | { type: 'CHANGES_SAVED'; payload: { variantId: string; instructions: Array<{ selector: string; op: string; value: any }> } }
  | { type: 'REQUEST_AI_VARY'; payload: { element: ElementEditData } }
  | { type: 'INSTRUCTION_ADDED'; payload: { selector: string; op: string; value: any } };

export type ParentMessage =
  | { type: 'SET_MODE'; payload: { mode: EditorMode; elementSelector?: string } }
  | { type: 'SELECT_ELEMENT'; payload: { selector: string } }
  | { type: 'SELECTION_SET'; payload: { selector: string } }
  | { type: 'SELECTION_CLEAR' }
  | { type: 'REVERT_PREVIEWS'; payload: { selector: string } }
  | { type: 'UNDO' }
  | { type: 'REDO' }
  | { type: 'SWITCH_VARIANT'; payload: { variantId: string } }
  | { type: 'SAVE_CHANGES' }
  | { type: 'RESET_PREVIEW' }
  | { type: 'APPLY_STYLE_PREVIEW'; payload: { selector: string; property: string; value: string } }
  // Specific instruction types - each with its own schema
  | { type: 'APPLY_TEXT_CHANGE'; payload: { selector: string; text: string; replay?: boolean } }
  | { type: 'HYDRATE_INSTRUCTIONS'; payload: { instructions: Array<{ selector: string; op: string; value: unknown }> } }
  | { type: 'APPLY_STYLE_CHANGES'; payload: { selector: string; styles: Record<string, string> } }
  | { type: 'APPLY_VISIBILITY_CHANGE'; payload: { selector: string; visible: boolean } }
  | { type: 'APPLY_IMAGE_CHANGE'; payload: { selector: string; src: string; alt?: string } }
  | { type: 'APPLY_REORDER'; payload: { selector: string; direction: 'up' | 'down' } }
  | { type: 'APPLY_DELETE'; payload: { selector: string } }
  | { type: 'APPLY_DUPLICATE'; payload: { selector: string } }
  | { type: 'CANCEL_EDIT' }
  | { type: 'CLOSE_EDITOR' }
  | { type: 'REQUEST_READY' }
  | { type: 'HIGHLIGHT_ELEMENTS'; payload: { selectors: string[]; color?: string } }
  | { type: 'SCROLL_TO_ELEMENT'; payload: { selector: string } };

// ============================================================================
// Origin tolerance helpers
// ============================================================================

/**
 * Compute the set of origins the ParentBridge will trust for a configured
 * iframe origin.
 *
 * A stored experiment page URL (e.g. `https://customer.com/...`) frequently
 * redirects to a canonical host inside the iframe (`https://www.customer.com`,
 * or `http://` → `https://`). The postMessage handshake validates the iframe's
 * *actual* origin, so we accept the small, well-defined set of canonical-host
 * siblings derived purely from the configured origin — never a wildcard, and
 * never a different registrable domain.
 *
 * Rules (all derived from the URL, no per-domain code):
 * - the exact configured origin
 * - its `www.`-toggled sibling (add `www.` if absent, strip it if present)
 * - the `https` upgrade of an `http` origin (and the `www` sibling of that)
 */
export function computeAllowedOrigins(iframeOrigin: string): Set<string> {
  const allowed = new Set<string>();
  if (!iframeOrigin || iframeOrigin === '*') {
    if (iframeOrigin) allowed.add(iframeOrigin);
    return allowed;
  }

  const addWithWwwSibling = (origin: string) => {
    try {
      const url = new URL(origin);
      allowed.add(url.origin);
      const host = url.hostname;
      const sibling = host.startsWith('www.') ? host.slice(4) : `www.${host}`;
      const siblingUrl = new URL(url.origin);
      siblingUrl.hostname = sibling;
      allowed.add(siblingUrl.origin);
    } catch {
      // Non-URL origin (shouldn't happen) — keep the raw value.
      allowed.add(origin);
    }
  };

  addWithWwwSibling(iframeOrigin);

  // https upgrade of an http origin (+ its www sibling)
  try {
    const url = new URL(iframeOrigin);
    if (url.protocol === 'http:') {
      url.protocol = 'https:';
      addWithWwwSibling(url.origin);
    }
  } catch {
    // ignore
  }

  return allowed;
}

// ============================================================================
// Iframe Bridge - Runs inside the iframe (editor logic side)
// ============================================================================

type BridgeHandler = (payload: any) => void;

export class IframeBridge {
  private parentOrigin: string;
  private messageHandlers: Map<string, Set<BridgeHandler>> = new Map();
  private _revCounter = 0;
  private _lastParentRev = 0;

  constructor(parentOrigin: string) {
    this.parentOrigin = parentOrigin;
    this.setupListener();
  }

  private setupListener() {
    window.addEventListener('message', (event) => {
      // Validate origin
      if (event.origin !== this.parentOrigin && this.parentOrigin !== '*') {
        console.warn('[IframeBridge] Message from untrusted origin:', event.origin, 'expected:', this.parentOrigin);
        return;
      }

      const message = event.data as ParentMessage & { _rev?: number };

      // Drop stale messages based on revision
      if (message._rev !== undefined) {
        if (message._rev < this._lastParentRev) {
          console.warn('[IframeBridge] Dropping stale message:', message.type, message._rev, '< last:', this._lastParentRev);
          return;
        }
        this._lastParentRev = message._rev;
      }

      // Emit to registered handlers
      this.emit(message.type, message);
    });
  }

  /**
   * Send message to parent window
   */
  send(message: EditorMessage) {
    const stamped = { ...message, _rev: ++this._revCounter };
    window.parent.postMessage(stamped, this.parentOrigin);
  }

  /**
   * Register handler for specific message type
   */
  on(eventType: string, callback: BridgeHandler) {
    if (!this.messageHandlers.has(eventType)) {
      this.messageHandlers.set(eventType, new Set());
    }
    this.messageHandlers.get(eventType)!.add(callback);
  }

  /**
   * Unregister handler
   */
  off(eventType: string, callback: BridgeHandler) {
    this.messageHandlers.get(eventType)?.delete(callback);
  }

  /**
   * Emit event to registered handlers
   */
  private emit(eventType: string, message: any) {
    const handlers = this.messageHandlers.get(eventType);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(message);
        } catch (error) {
          console.error('[IframeBridge] Error in handler:', error);
        }
      });
    }
  }

  /**
   * Clean up
   */
  destroy() {
    this.messageHandlers.clear();
  }
}

// ============================================================================
// Parent Bridge - Runs in parent window (editor UI side)
// ============================================================================

export class ParentBridge {
  /**
   * ES private (not TS `private`, which is erased at runtime) so the
   * cross-origin iframe Window is never an enumerable own property.
   * React dev builds serialize prop objects with `for...in` + a
   * `value.$$typeof` read (react-dom `addObjectToProperties`); reading any
   * named property off a cross-origin Window throws a SecurityError, and
   * because this bridge is passed as a prop that throw lands mid-commit and
   * takes down the whole editor tree.
   */
  #iframeWindow: Window;
  private iframeOrigin: string;
  /**
   * Origins we accept inbound messages from — the configured origin plus its
   * canonical-host siblings (www toggle, https upgrade). Once the iframe's real
   * origin is observed we lock `iframeOrigin` to it so outbound `send()` uses a
   * targetOrigin the browser won't silently drop.
   */
  private allowedOrigins: Set<string>;
  private originLocked = false;
  private _lastRejectedOrigin: string | null = null;
  private messageHandlers: Map<string, Set<BridgeHandler>> = new Map();
  private isConnected = false;
  private readyPromise: Promise<void>;
  private readyResolve?: () => void;
  private _revCounter = 0;
  private _lastIframeRev = 0;

  constructor(iframe: HTMLIFrameElement, iframeOrigin: string) {
    if (!iframe.contentWindow) {
      throw new Error('[ParentBridge] Iframe contentWindow not available');
    }
    
    this.#iframeWindow = iframe.contentWindow;
    this.iframeOrigin = iframeOrigin;
    this.allowedOrigins = computeAllowedOrigins(iframeOrigin);
    
    // Create ready promise
    this.readyPromise = new Promise((resolve) => {
      this.readyResolve = resolve;
    });
    
    this.setupListener();
    
    // Request ready status from iframe to handle race condition where
    // iframe sent READY before this bridge was created
    this.requestReady();
  }

  /**
   * The last http(s) origin that was rejected as untrusted. Surfaced for
   * diagnostics (e.g. a redirect to a different registrable domain that we
   * intentionally never auto-trust) so timeouts aren't opaque.
   */
  get lastRejectedOrigin(): string | null {
    return this._lastRejectedOrigin;
  }

  /** The origin currently used as the outbound postMessage targetOrigin. */
  get currentOrigin(): string {
    return this.iframeOrigin;
  }

  /**
   * Post a message to the iframe using a safe targetOrigin.
   *
   * Before the origin is locked we don't yet know which canonical sibling the
   * iframe actually redirected to, so a single post to the configured origin is
   * silently dropped by the browser after an apex↔www / http→https redirect —
   * which used to strand the missed-READY recovery path. Instead we post once
   * per allowed origin: `postMessage` only delivers to the frame whose *actual*
   * origin matches the given targetOrigin, so the correct sibling receives it
   * and the rest are dropped. We never use `'*'`, so no message can leak to an
   * unintended origin. Once locked we post only to the confirmed origin.
   */
  private postToIframe(payload: any) {
    if (this.originLocked) {
      this.#iframeWindow.postMessage(payload, this.iframeOrigin);
      return;
    }
    for (const origin of this.allowedOrigins) {
      if (origin === '*') continue; // never broadcast to a wildcard
      try {
        this.#iframeWindow.postMessage(payload, origin);
      } catch {
        // Ignore — a non-matching origin just isn't delivered.
      }
    }
  }

  /**
   * Request the iframe to send its READY status.
   * This handles the race condition where the iframe may have sent READY
   * before the ParentBridge was initialized.
   */
  private requestReady() {
    this.postToIframe({ type: 'REQUEST_READY' });
  }

  private setupListener() {
    window.addEventListener('message', (event) => {
      // Validate origin against the allowed set (configured origin + canonical
      // siblings). This tolerates the common apex↔www / http→https redirect the
      // customer site performs inside the iframe.
      if (!this.allowedOrigins.has(event.origin)) {
        // Only log if it's not from other sources (like browser extensions)
        if (event.origin.includes('http')) {
          this._lastRejectedOrigin = event.origin;
          console.warn('[ParentBridge] Message from unexpected origin:', event.origin, 'allowed:', Array.from(this.allowedOrigins).join(', '));
        }
        return;
      }

      // Lock onto the iframe's actual origin the first time we hear from it, so
      // outbound send() uses a targetOrigin the browser will actually deliver.
      if (!this.originLocked) {
        this.originLocked = true;
        this.iframeOrigin = event.origin;
      }

      const message = event.data as EditorMessage & { _rev?: number };

      // Drop stale messages based on revision
      if (message._rev !== undefined) {
        if (message._rev < this._lastIframeRev) {
          console.warn('[ParentBridge] Dropping stale message:', message.type, message._rev, '< last:', this._lastIframeRev);
          return;
        }
        this._lastIframeRev = message._rev;
      }

      // Handle READY message specially
      if (message.type === 'READY') {
        this.isConnected = true;
        if (this.readyResolve) {
          this.readyResolve();
        }
      }
      
      // Emit to registered handlers
      this.emit(message.type, message.type === 'READY' ? undefined : (message as any).payload);
    });
  }

  /**
   * Send message to iframe
   */
  send(message: ParentMessage) {
    if (!this.isConnected) {
      console.warn('[ParentBridge] Not connected yet, queuing message:', message.type);
    }
    const stamped = { ...message, _rev: ++this._revCounter };
    // Before lock, broadcast across allowed sibling origins (only the matching
    // frame receives it) so pre-READY retries survive an in-iframe redirect.
    this.postToIframe(stamped);
  }

  /**
   * Register handler for specific message type
   */
  on(eventType: string, callback: BridgeHandler) {
    if (!this.messageHandlers.has(eventType)) {
      this.messageHandlers.set(eventType, new Set());
    }
    this.messageHandlers.get(eventType)!.add(callback);
  }

  /**
   * Unregister handler
   */
  off(eventType: string, callback: BridgeHandler) {
    this.messageHandlers.get(eventType)?.delete(callback);
  }

  /**
   * Emit event to registered handlers
   */
  private emit(eventType: string, payload: any) {
    const handlers = this.messageHandlers.get(eventType);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(payload);
        } catch (error) {
          console.error('[ParentBridge] Error in handler:', error);
        }
      });
    }
  }

  /**
   * Wait for iframe to be ready
   */
  async waitForReady(timeout = 10000): Promise<void> {
    const timeoutPromise = new Promise<void>((_, reject) => {
      setTimeout(() => reject(new Error('Iframe ready timeout')), timeout);
    });
    
    return Promise.race([this.readyPromise, timeoutPromise]);
  }

  /**
   * Clean up
   */
  destroy() {
    this.messageHandlers.clear();
  }
}

